import { useMemo, useEffect, useState } from 'react'
import { Box, Drawer, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectBeacon4VisibleSeries,
  toggleBeacon4Series,
  resetBeacon4HiddenSeries,
} from '../../reducers/Beacon4FiltersReducer'
import { selectBeacon4BlendedSeries } from '../../reducers/Beacon4KpiDataReducer'

const TECH_DEFS = [
  { key: '2G',      color: '#1a3a6e', dash: false },
  { key: '3G',      color: '#4fc3f7', dash: false },
  { key: '4G',      color: '#f57c00', dash: false },
  { key: '5G',      color: '#ffb74d', dash: false },
  { key: 'Blended', color: '#212121', dash: true  },
]

const RAG_DEFS = [
  { key: 'RAG Green', color: '#2e7d32', dash: true },
  { key: 'RAG Red',   color: '#c62828', dash: true },
]

function LegendItem({ d, isHidden, onClick, shimmer }) {
  return (
    <Box onClick={() => onClick(d.key)} sx={{
      position: 'relative', overflow: 'hidden',
      display: 'flex', alignItems: 'center', gap: 1.25,
      px: 1, py: 0.75, borderRadius: 1, cursor: 'pointer',
      opacity: isHidden ? 0.3 : 1,
      transition: 'opacity 0.2s, background 0.15s',
      '&:hover': { bgcolor: 'action.hover' },
      // Shimmer sweep on first load
      '&::after': shimmer ? {
        content: '""',
        position: 'absolute',
        top: 0, left: '-100%',
        width: '60%', height: '100%',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
        transform: 'skewX(-20deg)',
        animation: `b4p-shimmer 2.2s linear infinite`,
        animationDelay: `${d._delay ?? 0}ms`,
      } : {},
      '@keyframes b4p-shimmer': {
        '0%':   { left: '-100%' },
        '100%': { left: '150%'  },
      },
    }}>
      <Box sx={{
        width: 24, flexShrink: 0,
        height: d.dash ? 0 : 3, borderRadius: 1,
        bgcolor: d.dash ? 'transparent' : d.color,
        borderTop: d.dash ? `2px dashed ${d.color}` : 'none',
      }} />
      <Typography sx={{ fontSize: 12, fontWeight: isHidden ? 400 : 600, color: isHidden ? 'text.disabled' : d.color }}>
        {d.key}
      </Typography>
    </Box>
  )
}

export default function Beacon4LegendPanel() {
  const dispatch      = useDispatch()
  const visibleSeries = useSelector(selectBeacon4VisibleSeries)
  const blendedSeries = useSelector(selectBeacon4BlendedSeries)
  const handleClick   = (key) => dispatch(toggleBeacon4Series(key))

  const [hasData, setHasData] = useState(false)

  const availableKeys = useMemo(() => {
    const keys = new Set()
    Object.values(blendedSeries).forEach(dto => (dto?.series ?? []).forEach(s => keys.add(s.key)))
    return keys
  }, [blendedSeries])

  useEffect(() => {
    if (availableKeys.size > 0) setHasData(true)
  }, [availableKeys])

  const handleItemClick = (key) => { handleClick(key) }

  const showShimmer = hasData && visibleSeries === null
  const showHint    = hasData && visibleSeries === null

  const visibleTechs = availableKeys.size > 0
    ? TECH_DEFS.filter(d => availableKeys.has(d.key)).map((d, i) => ({ ...d, _delay: i * 300 }))
    : TECH_DEFS.map((d, i) => ({ ...d, _delay: i * 300 }))

  return (
    <Drawer anchor="right" open variant="persistent" sx={{
      flexShrink: 0,
      '& .MuiDrawer-paper': {
        width: 190, position: 'relative', height: '100%',
        border: 'none', borderLeft: '1px solid', borderColor: 'divider',
        bgcolor: '#fff', overflow: 'hidden',
      },
    }}>
      <Box sx={{ p: 1.5, display: 'flex', flexDirection: 'column', gap: 0.25, flex: 1 }}>

        {/* Hint — top, prominent, disappears after first interaction */}
        {showHint && (
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.25, py: 1, mb: 1, borderRadius: 1.5,
            bgcolor: 'primary.main',
            animation: 'b4p-hint 1.6s ease-in-out infinite',
            '@keyframes b4p-hint': {
              '0%':   { opacity: 0.8, transform: 'translateX(0px)'  },
              '50%':  { opacity: 1,   transform: 'translateX(4px)'  },
              '100%': { opacity: 0.8, transform: 'translateX(0px)'  },
            },
          }}>
            <Box component="svg" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth={2.5}
              sx={{ width: 16, height: 16, flexShrink: 0 }}>
              <path d="M5 12h14" />
              <path d="m13 5 7 7-7 7" />
            </Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#fff', lineHeight: 1.3 }}>
              Click a label to filter
            </Typography>
          </Box>
        )}

        {/* Technologies */}
        <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mb: 0.5, mt: 0.5 }}>
          Technologies
        </Typography>
        {visibleTechs.map(d => (
          <LegendItem
            key={d.key} d={d}
            isHidden={visibleSeries !== null && !visibleSeries.includes(d.key)}
            onClick={handleItemClick}
            shimmer={showShimmer}
          />
        ))}

        {/* RAG Thresholds */}
        {availableKeys.size > 0 && (
          <>
            <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', px: 1, mt: 1.5, mb: 0.5 }}>
              RAG Thresholds
            </Typography>
            {RAG_DEFS.map((d, i) => (
              <LegendItem
                key={d.key} d={{ ...d, _delay: (visibleTechs.length + i) * 300 }}
                isHidden={visibleSeries !== null && !visibleSeries.includes(d.key)}
                onClick={handleItemClick}
                shimmer={showShimmer}
              />
            ))}
          </>
        )}
      </Box>

      {/* Bottom area: hint OR show all */}
      <Box sx={{ px: 1.5, pb: 1.5 }}>
        {visibleSeries !== null ? (
          <Box onClick={() => dispatch(resetBeacon4HiddenSeries())} sx={{
            fontSize: 11, fontWeight: 600, color: 'primary.main', cursor: 'pointer',
            textAlign: 'center', py: 0.75, borderRadius: 1,
            border: '1px solid', borderColor: 'primary.light',
            '&:hover': { bgcolor: 'primary.light' }, transition: 'all .15s',
          }}>
            ✕ Show all
          </Box>
        ) : null}
      </Box>
    </Drawer>
  )
}